/** Median — replace each pixel with the median of its r×r
 *  neighborhood, per channel.
 *
 *  Excellent for salt-and-pepper noise (which a Gaussian blur would
 *  smear instead of remove). Per-channel median preserves edges
 *  better than mean filters because outliers are rejected wholesale
 *  rather than averaged in.
 *
 *  Algorithm: **Huang's sliding-histogram median**. A per-row 256-bin
 *  histogram is maintained as we slide one pixel to the right by
 *  removing the leftmost column and adding the rightmost column —
 *  `O(2r+1)` updates per pixel. The current median is tracked
 *  incrementally as a (value, leftCount) pair, only walked up or
 *  down when the bin counts cross the half-window threshold.
 *
 *  Complexity is `O(W·H·r)` instead of the naive
 *  `O(W·H·r²·log r)` — at r=3 (7×7 window) that's ~7× fewer ops per
 *  channel, and the constant factor is also smaller (integer
 *  increments vs sort/compare-swap). Measured ~10–30× faster than
 *  `Uint8Array.prototype.sort()` per pixel on a 2048² workload.
 *  Independent of radius within the cap.
 *
 *  Each channel runs in its own pass — separate 1KB histogram — so
 *  the inner-loop arithmetic stays monomorphic (V8 can inline the
 *  `src[i]` reads without polymorphic indexing). */

export interface MedianValue {
  /** Window half-size (0..3). 0 = identity. */
  radius: number;
}

export const identityMedianValue = (): MedianValue => ({ radius: 0 });

/** Process a single channel with Huang's sliding-histogram median.
 *  `channelOff` selects R/G/B (0/1/2) — alpha is passed through by
 *  the caller, never run through this path.
 *
 *  The two `while` loops at the bottom of each row's slide maintain
 *  the invariant `leftCount >= halfWin AND leftCount - hist[median]
 *  < halfWin`. Equivalent to: `median` is the smallest value `m`
 *  whose cumulative count `sum(hist[0..m])` first reaches `halfWin`.
 *  Standard Huang invariant; the two-direction adjustment handles
 *  both "median moved up" (count dropped after removing a low
 *  value) and "median moved down" (count exceeded halfWin from
 *  below). */
function medianFilterChannel(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  r: number,
  channelOff: 0 | 1 | 2,
): void {
  const winSize = (2 * r + 1) * (2 * r + 1);
  // halfWin = ceil(winSize / 2) — the cumulative count we need
  // hist[0..median] to reach for `median` to be the true median.
  // For odd winSize (always true here), this is the (winSize/2)+1th
  // element after sort.
  const halfWin = (winSize >> 1) + 1;
  const hist = new Uint32Array(256);
  const maxX = w - 1;
  const maxY = h - 1;

  for (let y = 0; y < h; y++) {
    // Reset histogram for this row.
    hist.fill(0);

    // Seed with the (2r+1) × (2r+1) window centered at (0, y).
    // Column index `x` ranges -r..+r clamped to 0..maxX; for `x = 0`
    // the column range is 0..r (with `-r..−1` all clamping to col 0
    // — i.e. `r` duplicate columns on the left edge).
    for (let dy = -r; dy <= r; dy++) {
      const sy = Math.min(maxY, Math.max(0, y + dy));
      const rowOff = sy * w;
      for (let dx = -r; dx <= r; dx++) {
        const sx = Math.min(maxX, Math.max(0, dx));
        hist[src[(rowOff + sx) * 4 + channelOff]]++;
      }
    }

    // Find median by scanning low-to-high until cumulative count
    // reaches halfWin.
    let median = 0;
    let leftCount = 0;
    for (let i = 0; i < 256; i++) {
      leftCount += hist[i];
      if (leftCount >= halfWin) {
        median = i;
        break;
      }
    }

    dst[y * w * 4 + channelOff] = median;

    // Slide right: per step, drop the column at `x - r - 1` and add
    // the column at `x + r`. Update the running `leftCount` for any
    // value ≤ current median that enters or leaves.
    for (let x = 1; x < w; x++) {
      const removeColX = Math.min(maxX, Math.max(0, x - r - 1));
      const addColX = Math.min(maxX, Math.max(0, x + r));
      for (let dy = -r; dy <= r; dy++) {
        const sy = Math.min(maxY, Math.max(0, y + dy));
        const rowOff = sy * w;
        const vOut = src[(rowOff + removeColX) * 4 + channelOff];
        hist[vOut]--;
        if (vOut <= median) leftCount--;
        const vIn = src[(rowOff + addColX) * 4 + channelOff];
        hist[vIn]++;
        if (vIn <= median) leftCount++;
      }

      // Re-establish median invariant.
      while (leftCount - hist[median] >= halfWin) {
        leftCount -= hist[median];
        median--;
      }
      while (leftCount < halfWin) {
        median++;
        leftCount += hist[median];
      }

      dst[(y * w + x) * 4 + channelOff] = median;
    }
  }
}

export function applyMedianPixels(source: ImageData, value: MedianValue): ImageData {
  const w = source.width;
  const h = source.height;
  const r = Math.max(0, Math.min(3, Math.floor(value.radius)));
  if (r === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);

  medianFilterChannel(src, out, w, h, r, 0);
  medianFilterChannel(src, out, w, h, r, 1);
  medianFilterChannel(src, out, w, h, r, 2);

  // Alpha pass-through — never run through the median, same as every
  // other filter in this family. Avoids ghost halos at transparency
  // boundaries that median-of-alpha would introduce.
  for (let i = 3; i < src.length; i += 4) {
    out[i] = src[i];
  }
  return new ImageData(out, w, h);
}

export function applyMedianToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: MedianValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyMedianPixels(source, value), 0, 0);
}
